export const SIGNUP_SOURCES = [
  'Meta Ad',
  'Organic',
  'Referral',
  'University',
  'VC Partner',
  'Startup Tool Partner',
  'Direct',
  'Email',
  'Unknown',
] as const;

export type SignupSource = (typeof SIGNUP_SOURCES)[number];

export interface SignupAttribution {
  source: SignupSource;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  campaignId?: string;
  adsetId?: string;
  adId?: string;
  originalReferrer?: string;
  firstLandingPage?: string;
}

export function parseSignupAttributionQuery(value: unknown): SignupAttribution | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string' || value.length > 8192) throw new Error('Invalid signup attribution');
  return parseSignupAttribution(JSON.parse(value));
}

/** Untrusted marketing data never confers roles, membership, or paid access. */
export function parseSignupAttribution(value: unknown): SignupAttribution | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid signup attribution');
  if (!('source' in value)) {
    throw new Error('Invalid signup source');
  }
  const source = SIGNUP_SOURCES.find(source => source === value.source);
  if (!source) throw new Error('Invalid signup source');
  const result: SignupAttribution = { source };
  const fields = [
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmContent',
    'campaignId',
    'adsetId',
    'adId',
    'originalReferrer',
    'firstLandingPage',
  ] as const;
  for (const key of fields) {
    const input = Reflect.get(value, key);
    if (input === undefined || input === null) continue;
    const isUrl = key === 'originalReferrer' || key === 'firstLandingPage';
    if (
      typeof input !== 'string' ||
      input.length > (isUrl ? 2048 : 256) ||
      [...input].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
    ) {
      throw new Error(`Invalid signup attribution ${key}`);
    }
    if (!input.trim()) continue;
    if (isUrl) {
      const url = new URL(input);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
        throw new Error(`Invalid signup attribution ${key}`);
      }
      // Do not retain arbitrary query strings, credentials or fragments containing personal data.
      result[key] = `${url.origin}${url.pathname}`;
    } else {
      result[key] = input.trim();
    }
  }
  return result;
}
